import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowTopRightOnSquareIcon, CheckIcon } from '@heroicons/react/24/outline';
import type { TaskBodyProps } from './index';
import { fetchPatientCard } from '../../../services/patientCard';
import { useManufacturerStatus } from '../../../hooks/useManufacturerStatus';
import { isSparkManufacturer as checkIsSparkManufacturer } from '../../../utils/manufacturer';
import { copyToClipboard } from '../../../utils/clipboard';
import { FEATURE_FLAGS } from '../../../featureFlags';
import { FilesTabContent } from '../../patient-files/FilesTabContent';
import { SparkRefinementWizard } from './spark/SparkRefinementWizard';

const CARD_STALE_MS = 60_000;

function parseDobIso(iso: string | null): { yyyy: string; mm: string; dd: string } | null {
    if (!iso) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!match) return null;
    return { yyyy: match[1] as string, mm: match[2] as string, dd: match[3] as string };
}

function formatDob(iso: string | null): string | null {
    const parts = parseDobIso(iso);
    if (!parts) return null;
    return `${parts.mm}/${parts.dd}/${parts.yyyy}`;
}

function ageFromIso(iso: string | null): number | null {
    const parts = parseDobIso(iso);
    if (!parts) return null;
    const now = new Date();
    let age = now.getFullYear() - Number(parts.yyyy);
    const monthDiff = now.getMonth() + 1 - Number(parts.mm);
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < Number(parts.dd))) age--;
    return age >= 0 ? age : null;
}

//  Copy-to-clipboard pill — admins copy these values into the Spark portal.

function CopyButton({ value }: { value: string }) {
    const [copied, setCopied] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(
        () => () => {
            if (timer.current !== null) clearTimeout(timer.current);
        },
        []
    );
    const onClick = async (e: React.MouseEvent): Promise<void> => {
        e.stopPropagation();
        if (!(await copyToClipboard(value))) return;
        setCopied(true);
        timer.current = setTimeout(() => setCopied(false), 1800);
    };
    const base =
        'inline-flex items-center gap-1 px-2 py-[3px] rounded-md border text-[11px] font-medium transition-colors whitespace-nowrap flex-shrink-0';
    const state = copied
        ? 'border-green-500 text-green-700 bg-green-50'
        : 'border-gray-300 text-gray-500 bg-white hover:border-blue-500 hover:text-blue-600';
    return (
        <button type="button" onClick={onClick} className={`${base} ${state}`}>
            {copied ? (
                <>
                    <CheckIcon className="w-3 h-3" /> Copied
                </>
            ) : (
                'Copy'
            )}
        </button>
    );
}

const LABEL_CLS =
    'text-[10px] font-semibold uppercase tracking-wider text-gray-500 w-[96px] flex-shrink-0';

function Row({
    label,
    value,
    copyValue,
    isLast
}: {
    label: string;
    value: string;
    copyValue?: string;
    isLast?: boolean;
}) {
    return (
        <div
            className={`flex items-center justify-between gap-3 py-1.5 ${isLast ? '' : 'border-b border-gray-100'}`}
        >
            <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className={LABEL_CLS}>{label}</span>
                <span className="text-xs text-gray-800 truncate">{value}</span>
            </div>
            <CopyButton value={copyValue ?? value} />
        </div>
    );
}

function PillRow({
    label,
    value,
    isLast
}: {
    label: string;
    value: string | null;
    isLast?: boolean;
}) {
    return (
        <div
            className={`flex items-center justify-between gap-3 py-1.5 ${isLast ? '' : 'border-b border-gray-100'}`}
        >
            <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className={LABEL_CLS}>{label}</span>
                {value ? (
                    <span className="inline-flex items-center px-2 py-[1px] rounded-full text-[11px] font-medium bg-blue-100 text-blue-800 whitespace-nowrap truncate">
                        {value}
                    </span>
                ) : (
                    <span className="text-xs text-gray-300 italic">-</span>
                )}
            </div>
        </div>
    );
}

function LinkRow({ label, url, isLast }: { label: string; url: string | null; isLast?: boolean }) {
    return (
        <div
            className={`flex items-center justify-between gap-3 py-1.5 ${isLast ? '' : 'border-b border-gray-100'}`}
        >
            <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className={LABEL_CLS}>{label}</span>
                {url ? (
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 min-w-0 text-xs text-[#3773A8] font-medium hover:underline"
                    >
                        <span className="truncate">{url}</span>
                        <ArrowTopRightOnSquareIcon className="w-3 h-3 flex-shrink-0" />
                    </a>
                ) : (
                    <span className="text-xs text-gray-300 italic">Not set</span>
                )}
            </div>
            {url && <CopyButton value={url} />}
        </div>
    );
}

function Cell({
    title,
    children,
    scroll = true
}: {
    title: string;
    children: React.ReactNode;
    scroll?: boolean;
}) {
    return (
        <div className="flex-1 min-w-0 min-h-0 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex-shrink-0">
                <span className="font-bold text-sm text-gray-800">{title}</span>
            </div>
            <div
                className={`flex-1 min-h-0 ${scroll ? 'overflow-y-auto scrollbar-hide px-5 py-3' : 'flex flex-col'}`}
            >
                {children}
            </div>
        </div>
    );
}

/**
 * "Create refinement in manufacturer" — task entry point. For Spark practices
 * with a usable integration, render the guided refinement wizard
 * (create refinement → upload photos → upload scans). Otherwise fall back to the
 * display-only manual confirmation task below.
 */
export function CreateRefinementInManufacturerTask(props: TaskBodyProps) {
    const { workCase: c } = props;
    const { data: card, isLoading } = useQuery({
        queryKey: ['patient-card', c.id],
        queryFn: () => fetchPatientCard(c.id),
        staleTime: CARD_STALE_MS,
        enabled: Boolean(c.id)
    });

    const isSparkManufacturer = checkIsSparkManufacturer(card?.treatment?.manufacturerSlug ?? null);
    // The guided wizard is gated behind a dark-launch flag (opt-in, disabled by
    // default); when off, Spark practices get the manual confirmation fallback below.
    const wizardEligible = FEATURE_FLAGS.SPARK_REFINEMENT_WIZARD_ENABLED && isSparkManufacturer;

    const { automationReady, isLoading: statusLoading } = useManufacturerStatus(c.id, {
        enabled: wizardEligible
    });

    const waitingForCard = isLoading && !card;
    if (waitingForCard || statusLoading) {
        return (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
                Loading…
            </div>
        );
    }

    if (wizardEligible && automationReady) {
        return <SparkRefinementWizard {...props} />;
    }
    return <ManualCreateRefinement {...props} />;
}

/**
 * Display-only confirmation task (manual fallback). Surfaces everything the
 * admin needs to create the refinement case in Spark by hand: copyable patient
 * identity, the manufacturer case ID + link, and the patient files. Advancing
 * fires the bare "Mark as created" transition to Ref-3.
 */
function ManualCreateRefinement({ workCase: c }: TaskBodyProps) {
    const { data: card } = useQuery({
        queryKey: ['patient-card', c.id],
        queryFn: () => fetchPatientCard(c.id),
        staleTime: CARD_STALE_MS,
        enabled: Boolean(c.id)
    });

    const fullName = card
        ? `${card.lastName ?? ''}, ${card.firstName ?? ''}`.replace(/^,\s*|,\s*$/g, '') || card.name
        : '';
    const dobIso = card?.dateOfBirth ?? null;
    const dobFormatted = formatDob(dobIso);
    const age = ageFromIso(dobIso);
    const dobDisplay = dobFormatted
        ? age !== null
            ? `${dobFormatted} (${age} yo)`
            : dobFormatted
        : '';

    const manufacturerLink = card?.treatment?.manufacturerLink ?? null;
    const manufacturerCaseId = card?.treatment?.manufacturerCaseId ?? null;

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 flex flex-col gap-3 px-4 pt-3 pb-3">
                {/* Patient info + manufacturer reference */}
                <div className="flex-shrink-0">
                    <Cell title="Patient Info">
                        <div className="grid grid-cols-2 gap-x-6 font-inter">
                            {/* Identity */}
                            <div className="flex flex-col">
                                {fullName && <Row label="Name" value={fullName} />}
                                {dobDisplay && (
                                    <Row
                                        label="DOB"
                                        value={dobDisplay}
                                        copyValue={dobFormatted ?? ''}
                                    />
                                )}
                                {card?.gender && <Row label="Gender" value={card.gender} />}
                                {card?.email && <Row label="Email" value={card.email} isLast />}
                            </div>
                            {/* Contact + manufacturer */}
                            <div className="flex flex-col">
                                {card?.phone && <Row label="Phone" value={card.phone} />}
                                <PillRow
                                    label="Aligner"
                                    value={card?.treatment?.manufacturer ?? null}
                                />
                                <Row
                                    label="Mfr. case ID"
                                    value={manufacturerCaseId ?? '-'}
                                    copyValue={manufacturerCaseId ?? ''}
                                />
                                <LinkRow label="Mfr. link" url={manufacturerLink} isLast />
                            </div>
                        </div>
                    </Cell>
                </div>

                {/* Files */}
                <div className="flex-1 min-h-0 flex gap-3">
                    <Cell title="Files" scroll={false}>
                        <FilesTabContent patientId={c.id} />
                    </Cell>
                </div>
            </div>
        </div>
    );
}
